import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProjectsHeader from '../components/ProjectsHeader';
import NavigationMenu from '../components/NavigationMenu';
import MainContentArea from '../components/MainContentArea';
import HeadlessReviewPage from '../pages/HeadlessReviewPage';
import HeadlessIntegrationsPage from '../pages/HeadlessIntegrationsPage';
import { useNotifications } from '../contexts/NotificationsContext';
import { buildMainNavLinks } from '../utils/mainNavLinks';
import '../styles/MainPage.css';
import './HeadlessLayout.css';

function isAdminUser(user) {
  return user?.accountType === 'admin' || user?.accountType === 'superadmin';
}

function HeadlessLayout() {
  const { user, loading } = useAuth();
  const { totalCount: horizonBadge, reviewCount, embeddingCount, schedulerErrorCount } = useNotifications();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('review');

  const getInitialOpen = () => {
    try {
      const match = document.cookie.match(/(?:^|; )nav_open=([^;]*)/);
      if (match) {
        const v = decodeURIComponent(match[1]);
        return v === '1' || v === 'true';
      }
    } catch (_) {}
    return false;
  };
  const [isMenuOpen, setIsMenuOpen] = useState(getInitialOpen);
  const [disableAnim, setDisableAnim] = useState(true);

  useEffect(() => {
    const id = requestAnimationFrame(() => setDisableAnim(false));
    return () => cancelAnimationFrame(id);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => {
      const next = !prev;
      try {
        document.cookie = `nav_open=${next ? '1' : '0'}; path=/; max-age=15552000`;
      } catch (_) {}
      return next;
    });
  };

  const matchWithWildcard = useMatch('/headless/:tab/*');
  const matchWithoutWildcard = useMatch('/headless/:tab');
  const matchRoot = useMatch('/headless');
  const match = matchWithWildcard || matchWithoutWildcard || matchRoot;

  useEffect(() => {
    if (!loading && user && !isAdminUser(user)) {
      navigate('/projects', { replace: true });
      return;
    }
    const tab = match?.params?.tab || '';
    if (!tab) {
      navigate('/headless/review', { replace: true });
      setActiveTab('review');
      return;
    }
    const allowed = ['review', 'integrations'];
    if (!allowed.includes(tab)) {
      navigate('/headless/review', { replace: true });
      setActiveTab('review');
      return;
    }
    if (tab !== activeTab) setActiveTab(tab);
  }, [match, user, loading, navigate, activeTab]);

  const renderContent = () => {
    switch (activeTab) {
      case 'integrations':
        return <HeadlessIntegrationsPage />;
      case 'review':
      default:
        return <HeadlessReviewPage />;
    }
  };

  const linkItems = useMemo(() => buildMainNavLinks({
    user,
    horizonBadge,
    embeddingCount,
    reviewCount,
    schedulerErrorCount,
  }), [user, horizonBadge, embeddingCount, reviewCount, schedulerErrorCount]);

  if (!loading && user && !isAdminUser(user)) {
    return null;
  }

  return (
    <div className="headless-layout">
      <ProjectsHeader isMenuOpen={isMenuOpen} onToggle={toggleMenu} title="Headless" />
      <div className="headless-container">
        <NavigationMenu
          isOpen={isMenuOpen}
          onToggle={toggleMenu}
          disableTransition={disableAnim}
          linkItems={linkItems}
          menuItems={[]}
          activeItem={activeTab}
          onItemClick={(id) => {
            setActiveTab(id);
            navigate(`/headless/${id}`);
          }}
          showUserMenu={true}
        />
        <MainContentArea isMenuOpen={isMenuOpen} disableTransition={disableAnim}>
          <div className="headless-main-content">
            {renderContent()}
          </div>
        </MainContentArea>
      </div>
    </div>
  );
}

export default HeadlessLayout;
